import { Container } from "@movicoders/di";
import { IRepository, DomainPage } from "@movicoders/domain";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useResourceContext } from "../../Resources";
import {
  GetImplementedEntityType,
  GetImplementedSearchPaginatedType,
  GetImplementedSearchType,
  ObjectType,
} from "../types";

export const useListDetailViewModel = <R extends IRepository<any, any, any>>(
  RType: ObjectType<R>
) => {
  const resource = useResourceContext();
  type E = GetImplementedEntityType<R>;
  type S = GetImplementedSearchType<R>;
  type SP = GetImplementedSearchPaginatedType<R>;

  const repository = Container.resolve<R>(RType as any);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [data, setData] = useState([] as E[]);
  const [selected, setSelected] = useState({} as E);
  const [dataPaginated, setDataPaginated] = useState({} as DomainPage<E>);

  const ID = window.location.pathname.split("/")[2];

  useEffect(() => {
    if (ID && ID !== "create") {
      goToDetail(ID);
    }
  }, [window.location.pathname]);

  const fetchOne = async (id: string) => {
    return new Promise<void>(async (res, rej) => {
      setLoadingDetail(true);
      try {
        const result = await repository.getById(id);
        setSelected(result);
        res();
      } catch (err) {
        setSelected({} as E);
        rej(err);
      } finally {
        setLoadingDetail(false);
      }
    });
  };

  const save = async (entity: E) => {
    return new Promise<void>(async (res, rej) => {
      try {
        setLoading(true);
        if (entity.id !== "-1") {
          await repository.update(entity);
        } else {
          entity.id = undefined;
          await repository.create(entity);
        }
        res();
      } catch (err) {
        rej(err);
      } finally {
        setLoading(false);
        goToList();
      }
    });
  };

  const goToList = async () => {
    setSelected({} as E);
    navigate(`/${resource}`);
  };

  const fetch = async (criteria: S) => {
    setLoading(true);
    await repository
      .get(criteria)
      .then((response: any) => setData(response))
      .finally(() => setLoading(false));
  };

  const getPaginated = async (criteria: SP) => {
    setLoading(true);
    await repository
      .getPaginated(criteria)
      .then((response: any) => setDataPaginated(response))
      .finally(() => setLoading(false));
  };

  const goToDetail = async (id: string) => {
    await fetchOne(id);
    navigate(`/${resource}/${id}`);
  };

  const create = () => {
    setSelected({ id: "-1" } as E);
    navigate(`/${resource}/create`);
  };

  const remove = async (id: string) => {
    await repository.remove(id);
  };

  return {
    selected,
    loadingDetail,
    save,
    goToList,
    fetchOne,
    data,
    dataPaginated,
    loading,
    fetch,
    setLoadingDetail,
    getPaginated,
    goToDetail,
    create,
    remove,
  };
};
